import React, { useEffect, useState } from "react";
import { getPatientAppointmentStatusCounts } from "../../api";

/**
 * AppointmentStatusCounts: how many appointments this patient has, per status.
 *
 * Counted live from the appointments collection, not taken from the
 * clinical-memory document. The memory figures are a by-product of the last AI
 * build, so they lag every booking, cancellation and no-show since then, and
 * they only exist as four behaviour buckets: a practice that renamed its
 * statuses, or added its own, never saw its own names in its own numbers.
 *
 * `variant="tiles"` is the compact panel's big-number row; `variant="inline"`
 * is the full panel's muted one-line strip.
 */
export default function AppointmentStatusCounts({ patientId, practiceId, variant = "tiles" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setError("");
    getPatientAppointmentStatusCounts(patientId, practiceId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err.message || "Could not load counts."); });
    return () => { cancelled = true; };
  }, [patientId, practiceId]);

  // Counts are supporting detail, not the point of the panel: a failure says
  // so quietly rather than pushing an alert above the history itself.
  if (error) return <div className="small text-muted mb-3">Appointment counts unavailable.</div>;
  if (!data) return null;
  if (!data.counts?.length) {
    return <div className="small text-muted mb-3">No appointments recorded for this patient.</div>;
  }

  if (variant === "inline") {
    return (
      <div className="d-flex gap-3 small text-muted mb-3 flex-wrap">
        <span><strong>Total:</strong> {data.total}</span>
        {data.counts.map((c) => (
          <span key={c.key}><strong>{c.label}:</strong> {c.count}</span>
        ))}
      </div>
    );
  }

  return (
    <div className="d-flex gap-4 mb-3 flex-wrap">
      <div>
        <div className="text-muted small text-uppercase">Total</div>
        <div className="fs-4 fw-semibold">{data.total}</div>
      </div>
      {data.counts.map((c) => (
        <div key={c.key}>
          <div className="text-muted small text-uppercase">{c.label}</div>
          <div className="fs-4 fw-semibold">{c.count}</div>
        </div>
      ))}
    </div>
  );
}
